import React, { useState, useCallback, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { useBarcodeScanner } from './useBarcodeScanner';
import PalletDetailCard, { PalletData } from './PalletDetailCard';
import ManualScanInput from './ManualScanInput';
import { api } from '../../config/api';

type StagingState = 'awaiting-pallet' | 'show-lane' | 'confirming' | 'success';
type ConnectionStatus = 'idle' | 'success' | 'error';

interface LaneContent {
  customer_code: string;
  pallet_count: number;
  staged_count: number;
}

interface LanePallet {
  sscc: string;
  job_number: string | null;
  pallet_number: number | null;
  quantity: number | null;
  status: string;
  stock_code: string | null;
  description: string | null;
  location: string | null;
}

interface LaneInfo {
  sscc: string;
  lane_code: string;
  customer_code: string;
  wave_id: string;
  lane_contents: LaneContent[];
  lane_pallets?: LanePallet[];
}

const OPERATOR_NAME_KEY = 'wms_operator_name';

/**
 * iPad-optimized staging screen for counterbalance drivers.
 *
 * Two-scan workflow:
 * 1. Scan picked pallet SSCC to see lane assignment
 * 2. Scan lane bollard barcode to confirm staging
 *
 * State machine: awaiting-pallet -> show-lane -> confirming -> success -> awaiting-pallet
 */
export default function StagingScreen() {
  const [stagingState, setStagingState] = useState<StagingState>('awaiting-pallet');
  const [status, setStatus] = useState<ConnectionStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [laneInfo, setLaneInfo] = useState<LaneInfo | null>(null);
  const [palletData, setPalletData] = useState<PalletData | null>(null);
  const [scannedSscc, setScannedSscc] = useState<string | null>(null);

  const operatorName = localStorage.getItem(OPERATOR_NAME_KEY) || '';

  // Refs for stale closure avoidance
  const stagingStateRef = useRef<StagingState>(stagingState);
  useEffect(() => { stagingStateRef.current = stagingState; }, [stagingState]);

  const scannedSsccRef = useRef<string | null>(null);
  useEffect(() => { scannedSsccRef.current = scannedSscc; }, [scannedSscc]);

  const laneInfoRef = useRef<LaneInfo | null>(null);
  useEffect(() => { laneInfoRef.current = laneInfo; }, [laneInfo]);

  const handlePalletScan = useCallback(async (sscc: string) => {
    setScannedSscc(sscc);
    scannedSsccRef.current = sscc;
    setErrorMessage(null);

    try {
      const response = await api.get(`/wms/pallets/${sscc}/lane-assignment`);
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        setLaneInfo(data);
        laneInfoRef.current = data;

        // Build PalletData from the scanned pallet details
        const sp = data.scanned_pallet;
        setPalletData({
          sscc: data.sscc,
          status: 'picked',
          job_number: sp?.job_number || null,
          customer_code: data.customer_code || null,
          customer_name: sp?.customer_name || data.customer_code || null,
          product_code: sp?.product_code || null,
          product_description: sp?.description || null,
          forme_number: sp?.forme_number || null,
          quantity: sp?.quantity || null,
          pallet_number: sp?.pallet_number ?? null,
          total_pallets: null,
          machine_code: sp?.machine_code || null,
          packed_at: sp?.packed_at || null,
          location: sp?.location || null,
          is_rescan: false,
          has_job_link: !!sp?.job_number,
        });

        setStagingState('show-lane');
        stagingStateRef.current = 'show-lane';
        setStatus('success');
      } else {
        setStatus('error');
        setErrorMessage(response.data.error || `Pallet ${sscc} not found`);
      }
    } catch (err: any) {
      setStatus('error');
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        `Pallet ${sscc} not found`;
      setErrorMessage(msg);
    }
  }, []);

  const handleLaneScan = useCallback(async (laneCode: string) => {
    const sscc = scannedSsccRef.current;
    if (!sscc) return;

    setStagingState('confirming');
    stagingStateRef.current = 'confirming';

    try {
      const response = await api.post(`/wms/pallets/${sscc}/stage`, {
        lane_code: laneCode,
        operator_name: operatorName,
        scan_method: 'barcode',
      });

      if (response.data.success) {
        setStagingState('success');
        stagingStateRef.current = 'success';
        setStatus('success');

        // Auto-transition back after 2000ms
        setTimeout(() => {
          setStagingState('awaiting-pallet');
          stagingStateRef.current = 'awaiting-pallet';
          setLaneInfo(null);
          laneInfoRef.current = null;
          setPalletData(null);
          setScannedSscc(null);
          scannedSsccRef.current = null;
          setStatus('idle');
        }, 2000);
      } else {
        setStagingState('show-lane');
        stagingStateRef.current = 'show-lane';
        setStatus('error');
        setErrorMessage(response.data.error || 'Failed to confirm staging');
      }
    } catch (err: any) {
      setStagingState('show-lane');
      stagingStateRef.current = 'show-lane';
      setStatus('error');
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        'Failed to confirm staging';
      setErrorMessage(msg);
    }
  }, [operatorName]);

  const handleScan = useCallback((code: string) => {
    const state = stagingStateRef.current;
    const isSSCC = code.length >= 18 && /^\d+$/.test(code);
    const isLaneBollard = code.startsWith('STAGE-L');

    if (isSSCC && (state === 'awaiting-pallet' || state === 'show-lane')) {
      // New pallet scan — look up lane assignment (allows switching pallets)
      handlePalletScan(code);
    } else if (state === 'show-lane' && isLaneBollard) {
      handleLaneScan(code);
    }
    // Otherwise ignore (wrong state or unrecognized format)
  }, [handlePalletScan, handleLaneScan]);

  useBarcodeScanner({ minLength: 4, onScan: handleScan });

  const statusColor =
    status === 'success'
      ? '#2E7D32'
      : status === 'error'
        ? '#D32F2F'
        : '#9E9E9E';

  // Extract lane number for display
  const laneNumber = laneInfo
    ? laneInfo.lane_code.replace('STAGE-L', '').replace(/^0+/, '')
    : '';

  return (
    <Box
      sx={{
        maxWidth: 800,
        mx: 'auto',
        width: '100%',
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Connection status indicator — layout provides the page title */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          mb: 2,
        }}
      >
        <Box
          sx={{
            width: 16,
            height: 16,
            borderRadius: '50%',
            backgroundColor: statusColor,
          }}
          data-testid="status-dot"
          aria-label={`Connection ${status}`}
        />
      </Box>

      {/* awaiting-pallet state */}
      {stagingState === 'awaiting-pallet' && (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 3,
            textAlign: 'center',
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: 600, color: '#CB6015', mb: 2 }}
          >
            SCAN PICKED PALLET
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Scan pallet SSCC to see lane assignment
          </Typography>
          <Box sx={{ mt: 2 }}>
            <ManualScanInput onSubmit={handleScan} placeholder="Type or paste SSCC / lane code..." />
          </Box>
        </Paper>
      )}

      {/* show-lane state */}
      {stagingState === 'show-lane' && laneInfo && (
        <Box>
          {palletData && (
            <Box sx={{ mb: 3 }}>
              <PalletDetailCard data={palletData} />
            </Box>
          )}

          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              textAlign: 'center',
              backgroundColor: '#FFFFFF',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
          >
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, color: '#CB6015', mb: 2 }}
            >
              TAKE TO LANE {laneNumber}
            </Typography>

            {/* Lane contents — individual pallet detail */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                Already in this lane:
              </Typography>
              {laneInfo.lane_pallets && laneInfo.lane_pallets.length > 0 ? (
                <Box
                  component="table"
                  sx={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    '& th, & td': {
                      px: 1,
                      py: 0.75,
                      textAlign: 'left',
                      fontSize: '0.85rem',
                      borderBottom: '1px solid #E0E0E0',
                    },
                    '& th': {
                      fontWeight: 600,
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                    },
                  }}
                >
                  <thead>
                    <tr>
                      <th>Job</th>
                      <th>Plt #</th>
                      <th>Item Code</th>
                      <th>Description</th>
                      <th style={{ textAlign: 'right' }}>Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {laneInfo.lane_pallets.map((p, idx) => (
                      <tr key={idx} style={p.sscc === scannedSscc ? { backgroundColor: '#FFF3E0' } : undefined}>
                        <td>{p.job_number || '—'}</td>
                        <td>{p.pallet_number ?? '—'}</td>
                        <td>{p.stock_code || '—'}</td>
                        <td>{p.description || '—'}</td>
                        <td style={{ textAlign: 'right' }}>{p.quantity?.toLocaleString() ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Box>
              ) : (
                <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Lane is empty
                </Typography>
              )}
            </Box>

            <Typography variant="body1" color="text.secondary">
              Scan lane bollard barcode to confirm
            </Typography>
          </Paper>
        </Box>
      )}

      {/* confirming state */}
      {stagingState === 'confirming' && laneInfo && (
        <Box>
          {palletData && (
            <Box sx={{ mb: 3 }}>
              <PalletDetailCard data={palletData} />
            </Box>
          )}

          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              textAlign: 'center',
              backgroundColor: '#FFFFFF',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              position: 'relative',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                borderRadius: '12px',
                zIndex: 1,
              }}
            >
              <CircularProgress sx={{ color: '#CB6015' }} />
            </Box>

            <Typography
              variant="h5"
              sx={{ fontWeight: 600, color: '#CB6015', mb: 2 }}
            >
              TAKE TO LANE {laneNumber}
            </Typography>

            <Typography variant="body1" color="text.secondary">
              Scan lane bollard barcode to confirm
            </Typography>
          </Paper>
        </Box>
      )}

      {/* success state */}
      {stagingState === 'success' && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            textAlign: 'center',
            backgroundColor: '#E8F5E9',
            border: '2px solid #2E7D32',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            transition: 'background-color 0.2s',
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: 600, color: '#2E7D32', mb: 2 }}
          >
            Staged at Lane {laneNumber}
          </Typography>
          <Chip
            label="Staged"
            sx={{ backgroundColor: '#2E7D32', color: '#FFFFFF' }}
          />
        </Paper>
      )}

      {/* Error snackbar */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={4000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          onClose={() => setErrorMessage(null)}
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
